import { Icon } from '@/design-system/components/icons/Icon';
import { H6, TextBody, TextCaption } from '@/design-system/components/typography';
import { Pressable, styled, SxProp, useDripsyTheme, View } from 'dripsy';
import * as Clipboard from 'expo-clipboard';
import { LinearGradient } from 'expo-linear-gradient';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useStore } from 'store/store';

export interface EmailSubmissionIntroductionComponentProps {
  sx?: SxProp;
  emailInstruction: string;
}
const MAX_WIDTH = 1300;
export const EmailSubmissionIntroductionComponent: React.FC<EmailSubmissionIntroductionComponentProps> = ({ sx, emailInstruction }) => {
  const { t } = useTranslation('global-bill-payment');
  const user = useStore();

  const Gradient = styled(LinearGradient)();
  const { theme } = useDripsyTheme();
  const { $radius1, $radius2 } = theme.space || ({} as any);

  const copyToClipboard = async () => {
    const link = emailInstruction;
    await Clipboard.setStringAsync(link);
  };

  return (
    <View sx={{ ...sxStyles.container, ...sx }}>
      <View sx={{ ...sxStyles.body, ...{ borderRadius: $radius2 } }}>
        <Gradient colors={['#3B6BA6', '#274673']} sx={sxStyles.background} start={{ x: 0, y: 0 }} end={{ x: 1, y: 1 }} />
        <View sx={sxStyles.content}>
          <View sx={{ flexDirection: 'row' }}>
            <View sx={sxStyles.leftContent}>
              <H6 bold={true} sx={sxStyles.text}>
                {t('global-bill-payment::instruction.text--banner-pay-bill-automatically')}
              </H6>
              <TextBody bold={true} sx={{ ...sxStyles.text, ...{ mt: '$6' } }}>
                {t('global-bill-payment::instruction.text--banner-how-it-work')}
              </TextBody>
            </View>
            <View>
              <Icon name="post" width={138} height={91} />
            </View>
          </View>
          <TextBody sx={{ ...sxStyles.text, ...{ mt: '$2' } }}>
            {t('global-bill-payment::instruction.text--banner-send-bill-to-email')}
          </TextBody>
          <TextBody sx={{ ...sxStyles.text, ...{ mt: '$3' } }}>{t('global-bill-payment::instruction.text--banner-if-you-prefer')}</TextBody>
          <View sx={{ ...sxStyles.copyLayout, ...{ borderRadius: $radius1 } }}>
            <TextCaption semiBold={true}>{emailInstruction}</TextCaption>
            <Pressable onPress={copyToClipboard}>
              <View sx={{ flexDirection: 'row', alignItems: 'center' }}>
                <Icon name="copy" color="$hyperlink" themeColor="$hyperlink" width={20} height={20} />
                <TextCaption semiBold={true} sx={{ ml: '$1', color: '$hyperlink' }}>
                  {t('global-bill-payment::upload-bill.button--copy-link')}
                </TextCaption>
              </View>
            </Pressable>
          </View>
        </View>
      </View>
    </View>
  );
};

const sxStyles: Record<string, SxProp> = {
  container: {
    backgroundColor: 'transparent'
  },
  body: {
    flexDirection: 'column',
    maxWidth: MAX_WIDTH,
    padding: '$5',
    overflow: 'hidden',
    shadowColor: '#1A000000',
    shadowOffset: {
      width: 2,
      height: 2
    },
    shadowOpacity: 0.1,
    shadowRadius: 10,
    elevation: 10
  },
  content: {
    flexDirection: 'column',
    zIndex: 1000
  },
  leftContent: {
    flexDirection: 'column',
    flex: 1
  },
  background: {
    position: 'absolute',
    left: 0,
    top: 0,
    right: 0,
    bottom: 0
  },
  text: {
    color: '$white'
  },
  copyLayout: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    px: '$4',
    py: '$3',
    mt: '$5',
    backgroundColor: '$backgroundPrimary'
  }
};
